import type { TimelineLaneAction } from "./timeline.lanes.actions";
import type { TimelineLaneState } from "./timeline.lanes.types";

export const DEFAULT_TIMELINE_LANE_STATE: TimelineLaneState = {
  lanesById: {},
  laneOrder: [],
  itemsByLaneId: {},
  capsulesById: {},
  capsuleOrder: [],
  dependencyRelations: [],
  syncBadgesByLaneId: {},
};

export function timelineLanesReducer(
  state: TimelineLaneState = DEFAULT_TIMELINE_LANE_STATE,
  action: TimelineLaneAction,
): TimelineLaneState {
  switch (action.type) {
    case "timeline/lanes/register": {
      const lane = action.lane;
      const exists = !!state.lanesById[lane.id];
      return {
        ...state,
        lanesById: {
          ...state.lanesById,
          [lane.id]: lane,
        },
        laneOrder: exists ? state.laneOrder : [...state.laneOrder, lane.id],
      };
    }

    case "timeline/lanes/registerMany": {
      const nextLanesById = { ...state.lanesById };
      const nextOrder = [...state.laneOrder];

      for (const lane of action.lanes) {
        const exists = !!nextLanesById[lane.id];
        nextLanesById[lane.id] = lane;
        if (!exists) nextOrder.push(lane.id);
      }

      return {
        ...state,
        lanesById: nextLanesById,
        laneOrder: nextOrder,
      };
    }

    case "timeline/lanes/update": {
      const current = state.lanesById[action.laneId];
      if (!current) return state;

      return {
        ...state,
        lanesById: {
          ...state.lanesById,
          [action.laneId]: {
            ...current,
            ...action.patch,
          } as typeof current,
        },
      };
    }

    case "timeline/lanes/reorder":
      return {
        ...state,
        laneOrder: action.laneOrder,
      };

    case "timeline/lanes/setItems":
      return {
        ...state,
        itemsByLaneId: {
          ...state.itemsByLaneId,
          [action.laneId]: action.items,
        },
      };

    case "timeline/lanes/upsertCapsule": {
      const capsule = action.capsule;
      const exists = !!state.capsulesById[capsule.id];
      return {
        ...state,
        capsulesById: {
          ...state.capsulesById,
          [capsule.id]: capsule,
        },
        capsuleOrder: exists ? state.capsuleOrder : [...state.capsuleOrder, capsule.id],
      };
    }

    case "timeline/lanes/setDependencyRelations":
      return {
        ...state,
        dependencyRelations: action.relations,
      };

    case "timeline/lanes/setSyncBadges": {
      const syncBadgesByLaneId = Object.fromEntries(
        action.badges.map((badge) => [badge.laneId, badge]),
      );
      return {
        ...state,
        syncBadgesByLaneId,
      };
    }

    default:
      return state;
  }
}